import clsx from "clsx";
import { AnimatePresence, motion } from "motion/react";
import {
  cloneElement,
  FC,
  ReactElement,
  useEffect,
  useId,
  useRef,
  useState,
} from "react";
import toast from "react-hot-toast";
import { Tooltip } from "react-tooltip";
import { Account, Address, isThor, THOR } from "rujira.js";
import { AccountProvider, Provider, Spinner, useLocalStorage } from "../..";
import { useGlobalModalContext } from "../../context/GlobalModal";
import { useTranslation } from "../../i18n";
import { Animated } from "../icons/Animated";
import { Eye, EyeSlash, LinkDisconnect, Locked, Plus } from "../icons/Icons";
import { NetworkIcon } from "../icons/NetworkIcon";
import { Fiat } from "../numbers/Fiat";
import { ResolveLink } from "./ResolveLink";
import type { V2PrivacyControls } from "../../privacy";
import { usePrivateAccountRecovery } from "../privacy/PrivateAccountRecovery";
import { PrivateSessionSettings } from "../privacy/PrivateSessionSettings";

export interface ActiveAccountProps {
  address: string;
  label: string;
  icon: ReactElement;
}

interface AccountsProps {
  provider: AccountProvider;
  routingElement: any;
  wallets: WalletProps[];
  ProviderIcon: FC<{
    provider: Provider.Key;
    selected: boolean;
    className?: string;
  }>;
  getValue: (account: Account) => bigint;
  activeAccount?: ActiveAccountProps;
  privateAccounts?: V2PrivacyControls;
  isPrivateMode?: boolean;
  customActions?: ReactElement[];
  isLoading?: boolean;
}

export const Accounts = ({
  provider,
  routingElement,
  wallets,
  ProviderIcon,
  getValue,
  activeAccount,
  privateAccounts,
  isPrivateMode,
  customActions,
}: AccountsProps): ReactElement => {
  const { t } = useTranslation("header");
  const { accounts: allAccounts, disconnectAll, selected } = provider;
  const [showBlur, setShowBlur] = useState(false);
  const [showBalance, setShowBalance] = useLocalStorage<string>(
    "rujira-show-balance",
    "true"
  );

  const accounts = allAccounts?.filter((a) => isThor(a.address));
  const privateRecovery = usePrivateAccountRecovery(
    privateAccounts,
    undefined,
    isPrivateMode
  );
  const publicProvider: AccountProvider = privateAccounts
    ? {
        ...provider,
        select: (account) => {
          const sameWallet =
            !!account &&
            account.address.address === selected?.address.address &&
            account.provider === selected?.provider;
          if (sameWallet && privateAccounts.showPublic)
            privateAccounts.showPublic();
          else privateAccounts.lock();
          provider.select(account);
        },
      }
    : provider;

  return (
    <>
      {!accounts || accounts.length === 0 ? (
        <ConnectAnimation routingElement={routingElement}>
          <Wallets
            provider={provider}
            wallets={wallets}
            routingElement={routingElement}
          />
        </ConnectAnimation>
      ) : (
        <MyAccount
          routingElement={routingElement}
          provider={provider}
          activeAccount={activeAccount}
          value={
            selected ? getValue(Account.fromAddress(selected.address)) : false
          }
          onHide={() => setShowBlur(false)}>
          {selected ? (
            <ResolveLink as={routingElement} to="portfolio" className="balance">
              <div className="flex ai-b">
                {showBalance === "true" ? (
                  <>
                    <Fiat
                      amount={getValue(Account.fromAddress(selected.address))}
                      decimals={12}
                      symbol="~$"
                      padSymbol={false}
                      className="color-white fs-26 condensed"
                    />
                    <button
                      className="transparent ml-1 color-grey hover-white"
                      onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        setShowBalance("false");
                        return false;
                      }}
                      style={{ marginRight: "-1rem" }}>
                      <Eye className="w-2 h-2" />
                    </button>
                  </>
                ) : (
                  <>
                    <span
                      className="fiat color-grey fs-22"
                      style={{ fontFamily: "monospace" }}>
                      ****
                    </span>
                    <button
                      className="transparent ml-1 color-grey hover-white"
                      onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        setShowBalance("true");
                        return false;
                      }}
                      style={{ marginRight: "-1rem" }}>
                      <EyeSlash className="w-2 h-2" />
                    </button>
                  </>
                )}
              </div>
              <p className="fs-12 condensed color-grey mt-0.5">
                {t("viewPortfolio")}
              </p>
            </ResolveLink>
          ) : null}

          {activeAccount && <ActiveAccount account={activeAccount} />}

          {activeAccount && accounts.length > 0 && (
            <p className="account-role-label">Signing wallet</p>
          )}

          {accounts.map((account) => (
            <AddressC
              key={`${account.address.address}:${account.provider}`}
              provider={publicProvider}
              ProviderIcon={ProviderIcon}
              account={account}
              selected={
                !activeAccount &&
                selected?.address.address === account.address.address &&
                selected.provider === account.provider
              }
            />
          ))}
          {privateRecovery}
          {privateAccounts && isPrivateMode && (
            <PrivateSessionSettings
              controls={privateAccounts}
              disabled={["onboarding", "processing"].includes(
                privateAccounts.status
              )}
            />
          )}

          <nav className="actions">
            {privateAccounts &&
              !privateAccounts.privateSessionLocked &&
              ["ready", "processing"].includes(privateAccounts.status) && (
                <button
                  type="button"
                  className="action transparent private-account-logout"
                  onClick={() => privateAccounts.lock()}>
                  <Locked />
                  <span>{t("lockPrivateMode")}</span>
                </button>
              )}
            <ResolveLink
              as={routingElement}
              className="action transparent"
              to="connect">
              <Plus />
              <span>{t("connectAnotherWallet")}</span>
            </ResolveLink>
            {customActions &&
              customActions.map((e, index) => cloneElement(e, { key: index }))}
            <button
              className="action transparent"
              onClick={() => {
                privateAccounts?.lock();
                disconnectAll();
              }}>
              <LinkDisconnect />
              <span>{t("disconnectAll")}</span>
            </button>
          </nav>
          {showBlur && <div className="blur" />}
        </MyAccount>
      )}
    </>
  );
};

const ConnectAnimation = ({
  children,
  routingElement,
}: {
  children: React.ReactNode;
  routingElement?: any;
}) => {
  const { t } = useTranslation("header");
  const [show, setShow] = useState(false);

  return (
    <div
      className="relative py-q1 no-underline"
      onMouseEnter={() => setShow(true)}
      onMouseLeave={() => setShow(false)}>
      <div className="rujira-header__connect">
        <div className="fs-14 fw-500 fs-15 condensed">{t("connect")}</div>
        <Animated
          name="connect"
          paused={!show}
          className={"w-3 h-3"}
          aria-label="Connect"
        />
      </div>
      <AnimatePresence>
        {show && (
          <motion.div
            className="rujira-header__popup right condensed w-32 text-center p-2"
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 4 }}
            exit={{ opacity: 0, y: -4 }}>
            <ResolveLink
              as={routingElement}
              className="flex dir-c action transparent"
              to="connect">
              <p className="color-white">{t("connectYourWallet")}</p>
              <small className="color-grey">{t("createOrConnectWallet")}</small>
            </ResolveLink>
            {children}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export interface WalletProps {
  key: string;
  label: string;
  provider?: Provider.Key;
  icon: ReactElement;
  url?: string;
}

interface WalletsProps {
  modalLayout?: boolean;
  provider: AccountProvider;
  routingElement?: any;
  wallets: WalletProps[];
  onConnectSuccess?: () => void;
}

export const Wallets = ({
  modalLayout = false,
  provider,
  wallets,
  routingElement,
  onConnectSuccess,
}: WalletsProps): ReactElement => {
  const { t } = useTranslation("header");
  const { connect, isAvailable } = provider;
  const { hideModal } = useGlobalModalContext();
  const connecting = useRef(false);
  const [connectingWallet, setConnectingWallet] = useState<string>();
  const rujiraWalletOrder = ["vultisig", "keplr"];
  const rujiraWallets = wallets
    .filter((wallet) => rujiraWalletOrder.includes(wallet.key))
    .sort(
      (a, b) =>
        rujiraWalletOrder.indexOf(a.key) - rujiraWalletOrder.indexOf(b.key)
    );
  const otherWallets = wallets.filter(
    (wallet) => !rujiraWalletOrder.includes(wallet.key)
  );

  const walletButton = (wallet: WalletProps) => {
    const disabled = wallet.provider ? !isAvailable(wallet.provider) : true;
    const pending = connectingWallet === wallet.key;
    return (
      <button
        key={wallet.label}
        type="button"
        id={`connect-${wallet.label}`}
        aria-label={wallet.label}
        aria-busy={pending}
        aria-disabled={!!connectingWallet}
        data-tooltip-content={wallet.label}
        data-tooltip-id="wallet-tip"
        className={clsx({
          "transparent block pointer relative": true,
          medium: modalLayout,
          "desaturate opacity-10": disabled,
        })}
        onClick={async () => {
          if (connecting.current) return;
          // Mobile wallet providers can arrive after the first render.
          if (!wallet.provider || !isAvailable(wallet.provider)) {
            if (wallet.url) window.open(wallet.url, "_blank");
            return;
          }
          connecting.current = true;
          setConnectingWallet(wallet.key);
          try {
            await connect(wallet.provider);
            if (onConnectSuccess) onConnectSuccess();
            else hideModal();
          } catch {
            // The account provider reports the error; keep the wallet chooser open.
          } finally {
            connecting.current = false;
            setConnectingWallet(undefined);
          }
        }}>
        <span
          style={{
            display: "block",
            visibility: pending ? "hidden" : undefined,
          }}>
          {wallet.icon}
        </span>
        {pending && (
          <span
            aria-hidden="true"
            style={{
              position: "absolute",
              inset: 0,
              display: "grid",
              placeItems: "center",
            }}>
            <Spinner />
          </span>
        )}
      </button>
    );
  };

  return (
    <>
      <div
        className={clsx("rujira-header__wallets", {
          "rujira-header__wallets--modal": modalLayout,
        })}>
        <span
          className="tag tag--borderless tag--primary no-shrink rujira-header__wallets-support"
          data-support-tooltip={t("rujiraSupportTooltip")}>
          {t("rujiraSupport")}
        </span>
        <div className="rujira-header__wallets-row">
          {rujiraWallets.map(walletButton)}
        </div>

        <hr className="rujira-header__wallets-divider" />

        <span
          className="tag tag--borderless tag--orange no-shrink rujira-header__wallets-support"
          data-support-tooltip={t("limitedSupportTooltip")}>
          {t("limitedSupport")}
        </span>
        <div className="rujira-header__wallets-row">
          {otherWallets.map(walletButton)}
        </div>
        <hr
          className={clsx({
            hr: true,
            "mt-0 mb-1": !modalLayout,
            "my-2": modalLayout,
          })}
        />
        <ResolveLink as={routingElement} to="/tou" className="terms mb-1">
          By connecting a wallet, you agree to Rujira's Terms of Use.
        </ResolveLink>
        <ResolveLink as={routingElement} to="/connect" onClick={hideModal}>
          {t("needHelpDeciding")}
        </ResolveLink>
      </div>
      <Tooltip id="wallet-tip" className="tooltip" />
    </>
  );
};

const MyAccount = ({
  provider,
  children,
  onHide,
  routingElement,
  value,
  activeAccount,
}: {
  provider: AccountProvider;
  children: React.ReactNode;
  onHide: () => void;
  routingElement?: any;
  value: bigint | false;
  activeAccount?: ActiveAccountProps;
}) => {
  const [show, setShow] = useState(false);
  const popupId = useId();
  const { accounts, selected } = provider;
  const [showBalance] = useLocalStorage<string>("rujira-show-balance", "true");

  useEffect(() => {
    onHide();
  }, [show]);

  return (
    <div
      className="relative py-q1"
      onMouseEnter={() => setShow(true)}
      onMouseLeave={(event) => {
        if (!event.currentTarget.contains(document.activeElement))
          setShow(false);
      }}
      onFocus={() => setShow(true)}
      onBlur={(event) => {
        const container = event.currentTarget;
        if (container.contains(event.relatedTarget)) return;
        // A click on a spot inside that takes no focus (a switch, a label)
        // sends focus to the page for a moment; the menu stays while the
        // pointer is over it instead of closing and opening again.
        if (event.relatedTarget === null && container.matches(":hover")) return;
        setShow(false);
      }}
      onKeyDown={(event) => {
        if (event.key === "Escape") {
          event.preventDefault();
          event.stopPropagation();
          event.currentTarget
            .querySelector<HTMLElement>(".rujira-header__pfp")
            ?.focus();
          setShow(false);
        }
      }}>
      <ResolveLink
        as={routingElement}
        to="portfolio"
        className="rujira-header__pfp"
        aria-label="Account menu and portfolio"
        aria-expanded={show}
        aria-controls={show ? popupId : undefined}
        onKeyDown={(event: React.KeyboardEvent) => {
          if (event.key === "ArrowDown" || event.key === " ") {
            event.preventDefault();
            setShow(true);
          }
        }}>
        {value !== false && showBalance === "true" && (
          <Fiat amount={value} symbol="~$" decimals={12} padSymbol={false} />
        )}
        {selected && activeAccount ? (
          <span className="rujira-header__active-account-icon">
            {activeAccount.icon}
          </span>
        ) : (
          selected && <NetworkIcon network={THOR} />
        )}
        {accounts?.length && !selected && (
          <Animated
            name="combined"
            paused
            color="white"
            aria-label="combined"
          />
        )}
        {provider.isLoading && <Spinner />}
      </ResolveLink>
      <AnimatePresence>
        {show && (
          <motion.div
            id={popupId}
            className="rujira-header__popup right condensed p-1.5 br-2"
            style={{ right: -8 }}
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 4 }}
            exit={{ opacity: 0, y: -4 }}>
            {children}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const ActiveAccount: FC<{ account: ActiveAccountProps }> = ({ account }) => {
  const { t } = useTranslation("header");
  const [hover, setHover] = useState(false);

  return (
    <div
      onMouseOver={() => setHover(true)}
      onMouseOut={() => setHover(false)}
      className="flex ai-c px-1 address address--selected address--private">
      <span className="address__wallet address__wallet--private">
        {account.icon}
      </span>
      <p
        className={clsx(
          "address__address address__address--private",
          account.address && "pointer"
        )}
        onClick={() => {
          // A rotating private account has no single address to copy.
          if (!account.address) return;
          toast.success(t("addressCopied"));
          navigator.clipboard.writeText(account.address);
        }}>
        {!account.address ? (
          <strong>{account.label}</strong>
        ) : hover ? (
          t("copyAddress")
        ) : (
          <>
            <strong>{account.label}</strong>
            <small>
              {account.address.substring(0, 8)} ... {account.address.slice(-8)}
            </small>
          </>
        )}
      </p>
      <span className="address__active-label">Active</span>
    </div>
  );
};

interface AddressProps {
  account: { address: Address; provider: Provider.Key };
  selected: boolean;
  provider: AccountProvider;
  ProviderIcon: FC<{
    provider: Provider.Key;
    selected: boolean;
    className?: string;
  }>;
}

const AddressC: FC<AddressProps> = ({
  account,
  selected,
  provider,
  ProviderIcon,
}) => {
  const { t } = useTranslation("header");
  const { select, disconnect } = provider;
  const [hover, setHover] = useState(false);

  return (
    <div
      onMouseOver={() => setHover(true)}
      onMouseOut={() => setHover(false)}
      className={clsx({
        "flex ai-c px-1 address": true,
        "address--selected": selected,
      })}>
      <NetworkIcon
        network={THOR}
        className="address__wallet"
        selected={selected || hover}
      />
      {/* <ProviderIcon provider={account.provider} selected={selected} /> */}
      <p
        className="address__address pointer"
        onClick={() => {
          if (selected) {
            toast.success(t("addressCopied"));
            navigator.clipboard.writeText(account.address.address);
          } else {
            select(account);
          }
        }}>
        {hover && selected ? (
          t("copyAddress")
        ) : (
          <>
            {account.address.address.substring(0, 8)} ...{" "}
            {account.address.address.slice(-8)}
          </>
        )}
      </p>

      {hover ? (
        <LinkDisconnect
          className="address__disconnect"
          onClick={() => disconnect(account.provider)}
        />
      ) : (
        <ProviderIcon
          className="address__disconnect"
          provider={account.provider}
          selected={selected}
        />
      )}
    </div>
  );
};
